import { Link } from "@tanstack/react-router";
import { useId } from "react";
import { ApiRequestError } from "../api/http";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { messageForError } from "../i18n/errors";
import { useT } from "../i18n/locale";

// Gli stati della pagina «Report» (piano M7, Task 3; `UX_SYSTEM.md`, «Matrice degli stati condivisi»; mock-up `loadingState`,
// `emptyState`, `errorState`, `noAccessHtml`): in caricamento la struttura resta e lo screen reader sente che si sta leggendo; un periodo
// vuoto dice che cosa manca e porta al «Mese» o a «Tutto il team», quando cambierebbero qualcosa; un errore non mostra numeri vecchi come
// se fossero di adesso e ha «Riprova»; senza l'area dei report nessun numero arriva al browser e la pagina dice perché. Nella «Guarda
// come…» una persona senza i report non apre la pagina: lo dice il guscio (RUOLI-19).

/** In caricamento: il titolo della sezione, la frase per lo screen reader e i gruppi vuoti («Campagne» non ha gruppi, come il mock-up). */
export function ReportsLoading({ title, groups = true }: { title: string; groups?: boolean }) {
  const t = useT();
  const titleId = useId();
  return (
    <>
      <section className="g-report-section" aria-labelledby={titleId}>
        <div className="g-report-head">
          <div>
            <h2 id={titleId}>{title}</h2>
            <p className="g-report-hint" role="status">
              {t("reports.states.loading")}
            </p>
          </div>
        </div>
        {groups ? (
          <div className="g-report-groups" aria-hidden="true">
            {[0, 1, 2, 3].map((group) => (
              <div key={group} className="g-report-group">
                <span className="g-report-skeleton" data-size="title" />
                {[0, 1, 2, 3, 4].map((line) => (
                  <span key={line} className="g-report-skeleton" />
                ))}
              </div>
            ))}
          </div>
        ) : null}
      </section>
      <div className="g-card g-report-skeleton-card" aria-hidden="true">
        <span className="g-report-skeleton" data-size="title" />
        {[0, 1, 2, 3, 4].map((line) => (
          <span key={line} className="g-report-skeleton" data-size="row" />
        ))}
      </div>
    </>
  );
}

type EmptyProps = {
  /** Di quale scheda: «Commerciali» dice chiamate e appuntamenti, «Campagne» Lead entrati e disco. */
  kind?: "sales" | "campaigns";
  /** Il periodo scritto, con la maiuscola. */
  range: string;
  /** Con persone scelte in «Di chi». */
  chosen: boolean;
  /** «Guarda il «Mese»», quando il periodo non è già il «Mese». */
  onMonth: (() => void) | null;
  onTeam: () => void;
};

/** Un periodo senza numeri: che cosa manca e, quando servono, «Guarda il «Mese»» e «Tutto il team». */
export function ReportsEmpty({ kind = "sales", range, chosen, onMonth, onTeam }: EmptyProps) {
  const t = useT();
  const titleId = useId();
  return (
    <section className="g-card g-report-empty" aria-labelledby={titleId}>
      <Icon name="info" size="md" />
      <h2 id={titleId}>{t("reports.states.emptyTitle")}</h2>
      <p>
        {kind === "campaigns"
          ? t(chosen ? "reports.states.emptyCampaignsChosen" : "reports.states.emptyCampaigns", { range })
          : t(chosen ? "reports.states.emptyChosen" : "reports.states.empty", { range })}
      </p>
      {onMonth || chosen ? (
        <div className="g-report-empty__actions">
          {onMonth ? (
            <Button variant="primary" onClick={onMonth}>
              {t("reports.states.goToMonth")}
            </Button>
          ) : null}
          {chosen ? <Button onClick={onTeam}>{t("reports.controls.wholeTeam")}</Button> : null}
        </div>
      ) : null}
    </section>
  );
}

type ErrorProps = {
  error: unknown;
  onRetry: () => void;
  /** «Guarda il «Mese»», per un «Personalizzato» che il server rifiuta (dopo oggi o più lungo di un anno). */
  onMonth: () => void;
  /** «Tutto il team», per una persona di «Di chi» che non c'è più; `null` senza persone scelte. */
  onTeam: (() => void) | null;
};

/**
 * Una lettura non riuscita. Senza risposta (la rete, il server) la frase del mock-up e «Riprova»; un periodo che il server rifiuta o una
 * persona scelta che non trova hanno il loro testo del catalogo e la via d'uscita accanto a «Riprova».
 */
export function ReportsError({ error, onRetry, onMonth, onTeam }: ErrorProps) {
  const t = useT();
  const titleId = useId();
  const code = error instanceof ApiRequestError ? error.code : null;
  const periodRefused = code === "report_period_invalid";
  const personGone = code === "not_found" && onTeam !== null;
  return (
    <section className="g-card g-report-empty" role="alert" aria-labelledby={titleId}>
      <Icon name="alert" size="md" />
      <h2 id={titleId}>{t("reports.states.errorTitle")}</h2>
      <p>{periodRefused || personGone ? messageForError(t, error) : t("reports.states.error")}</p>
      <div className="g-report-empty__actions">
        <Button variant="primary" onClick={onRetry}>
          {t("common.retry")}
        </Button>
        {periodRefused ? <Button onClick={onMonth}>{t("reports.states.goToMonth")}</Button> : null}
        {personGone && onTeam ? <Button onClick={onTeam}>{t("reports.controls.wholeTeam")}</Button> : null}
      </div>
    </section>
  );
}

/** Senza l'area dei report (`reports.view: none`), o quando il server li nega: il perché e «Torna alla Home». */
export function ReportsNoAccess() {
  const t = useT();
  const titleId = useId();
  return (
    <section className="g-card g-report-noaccess" aria-labelledby={titleId}>
      <Icon name="lock" size="md" />
      <h1 id={titleId} className="g-page-head__title">
        {t("reports.states.noAccessTitle")}
      </h1>
      <p>{t("reports.states.noAccess")}</p>
      <div>
        <Link to="/" className="g-button">
          {t("reports.states.home")}
        </Link>
      </div>
    </section>
  );
}
